import { type ApplyRequest, type ApplyStatus } from "@m8t-stack/platform-release";

/**
 * The decisions `m8t platform request-update --wait` makes while following a
 * request, extracted so they are testable AS decisions.
 *
 * This is the step that decides whether CD is green. Leaving the rules inside a
 * private method meant the only way to exercise them was to run a converge —
 * which is exactly the "first exercised in production" shape this whole branch
 * exists to remove.
 */

/** Terminal statuses, and whether each means the converge succeeded. */
export const TERMINAL: Record<string, boolean> = {
  success: true,
  failed: false,
  "rolled-back": false,
  held: false,
};

export function isTerminal(status: string): boolean {
  return status in TERMINAL;
}

export type PollVerdict =
  /** A row for a different request — ignore it entirely. */
  | { kind: "not-ours"; otherTarget: string }
  /** Ours, and finished. */
  | { kind: "settled"; row: ApplyRequest }
  /** Ours, still going. `where` is the human-readable position. */
  | { kind: "in-progress"; where: string };

/**
 * Pure. What does this polled row mean for the request we made?
 *
 * Two rules carry the weight:
 *
 * `not-ours` — a concurrent button press, or the reaper settling an older row,
 * must never be read as this request's outcome. Reporting someone else's
 * SUCCESS as ours is the dangerous half: it turns a build green for a converge
 * that never ran.
 *
 * `awaiting-engine-update` is progress, not a stall. The updater parks a request
 * there while replacing its own image, so a converge legitimately spans two
 * ticks; failing a build for that is failing it for working correctly.
 */
export function classifyPolledRow(row: ApplyRequest | null, target: string): PollVerdict | null {
  if (!row) return null;
  if (row.target !== target) return { kind: "not-ours", otherTarget: row.target };
  if (isTerminal(row.status)) return { kind: "settled", row };
  return { kind: "in-progress", where: positionLabel(row.status, row.phase) };
}

/** The human-readable position of a still-running request. Changes when the
 *  phase does, which is what lets the caller dedupe its log. */
function positionLabel(status: ApplyStatus, phase: string | null): string {
  return phase ? `${status} (${phase})` : status;
}

/**
 * Pure. The process exit code for a settled (or unsettled) request.
 *
 * A timeout — `settled === null` — is a FAILURE. Nothing was observed to
 * succeed, and exiting 0 on "we stopped looking" would report a converge that
 * may never have happened as a good build.
 */
export function outcomeExitCode(settled: ApplyRequest | null): number {
  if (!settled) return 1;
  return TERMINAL[settled.status] ? 0 : 1;
}

/** Pure. The status string to report, including the timeout case. */
export function outcomeStatus(settled: ApplyRequest | null): string {
  return settled?.status ?? "timed-out";
}
